import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { FilterImageCheckbox } from '../filterImageCheckbox/FilterImageCheckbox';
import helpers from '../../js/helpers';
import { isEmpty } from 'ramda';
import { Pages } from '../pages/Pages';
import classNames from 'classnames';
import type {
    CarType,
    SearchFilterState,
    ElasticAggregations,
    CarModel
} from '../../types/index';
import './CarModelSelector.scss';

export type FilterOption = {
    '@id'?: string;
    carTypes?: CarType[];
    model?: string;
    slug?: string;
    count?: number;
    key?: string | number;
    name?: string;
    imageUrl?: string;
    title?: string;
};
export type CarModelSelectorProps = {
    className?: string;
    locale?: string;
    onSelect: (
        key: keyof ElasticAggregations,
        value: string | number,
        event: React.MouseEvent<HTMLDivElement>
    ) => void;
    options: SearchFilterState['options'];
    selectedOptions: SearchFilterState['selectedOptions'];
    isLoading?: boolean;
    isScrollIntoViewEnabled?: boolean;
};

export const CarModelSelector = ({
    className,
    options,
    onSelect,
    selectedOptions,
    isLoading,
    isScrollIntoViewEnabled
}: CarModelSelectorProps) => {
    const [carTypes, setCarTypes] = useState<CarType[]>([]);
    const carTypesContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (
            !isEmpty(selectedOptions.carModels) &&
            !isLoading &&
            options.carTypes
        ) {
            const carModels = selectedOptions.carModels as string[];
            const carTypesForCurrentModelSelection =
                options.carModels?.find(model => model.key === carModels[0])
                    ?.carTypes ?? [];

            /**
             * Defensive code.
             * If isLoading prop is not uses, there may be a small render window,
             * where the above find method returns undefined. By filtering all falsy values,
             * we can check whether the find method truly returned CarTypes or not.
             */
            if (carTypesForCurrentModelSelection.filter(Boolean).length > 0) {
                setCarTypes(carTypesForCurrentModelSelection);
            } else {
                setCarTypes([]);
            }
        }
    }, [options, selectedOptions.carModels, isLoading]);

    /**
     * When SearchFilter mounts, URL may contain a car model filter.
     * This selection might be on another page of the slider.
     * Thus, we automatically jump to the page that contains the selected car model
     */
    const [activeCarModelPage, setActiveCarModelPage] = useState(0);
    useEffect(() => {
        const carModelPages =
            //@ts-ignore
            helpers.chunkify(options?.carModels, 5) as [CarModel[]];
        const selectedModel = selectedOptions.carModels
            ? selectedOptions.carModels[0]
            : '';

        carModelPages.forEach((page, index) => {
            if (
                selectedOptions.carModels &&
                page.findIndex(carModel => carModel.key === selectedModel) > -1
            ) {
                setActiveCarModelPage(index);
            }
        });
    }, []);

    useEffect(() => {
        if (selectedOptions.carModels && selectedOptions.carModels.length === 0) {
            setCarTypes([]);
        }
    }, [selectedOptions]);

    const handleCarSelect = (
        option: FilterOption,
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        onSelect('carModels', option.key as string, event);
    };

    /**
     * If Search Filter is in simplified mode (only showing car models & types),
     * we make sure that the car types will be scrolled into the view.
     * Thus, we need an intersection observer to notify us
     */
    const isCarTypesInView = useRef(false);

    const handleIntersecting = (event: IntersectionObserverEntry[]) => {
        isCarTypesInView.current = event[0].isIntersecting;
    };

    const intersectionObserver = new IntersectionObserver(handleIntersecting, {
        root: containerRef.current,
        rootMargin: '0px',
        threshold: 1
    });

    useLayoutEffect(() => {
        if (!isEmpty(carTypes) && isScrollIntoViewEnabled) {
            const searchFilterContainer =
                document.querySelector('.awc-search-filter');

            if (searchFilterContainer) {
                setTimeout(() => {
                    searchFilterContainer.scrollIntoView({
                        block: 'end',
                        inline: 'nearest',
                        behavior: 'smooth'
                    });
                }, 200);
            }
        }
    }, [carTypes]);

    useLayoutEffect(() => {
        if (carTypesContainerRef.current) {
            intersectionObserver.observe(carTypesContainerRef.current);
        }
    }, []);

    return (
        <div className={classNames('car-model-selector', className)} ref={containerRef}>
            <Pages className="car-model-selector__pages" activePage={activeCarModelPage}>
                {helpers
                    //@ts-ignore
                    .chunkify(options?.carModels, 5)
                    //@ts-ignore
                    .map((chunks: FilterOption[]) =>
                        chunks.map(option => (
                            <FilterImageCheckbox
                                className="car-model-selector__checkbox"
                                check={
                                    selectedOptions.carModels?.includes(
                                        option.key as string
                                    ) ?? false
                                }
                                hint={String(option.count)}
                                key={
                                    option.key +
                                    String(option.count) +
                                    option['@id']
                                }
                                label={option.name as string}
                                onCheck={(value, event) =>
                                    handleCarSelect(option, event)
                                }
                                src={option.imageUrl}
                                value={option.key}
                                disabled={option.count === 0}
                                title="car-model-checkbox"
                                withImage
                            />
                        ))
                    )}
            </Pages>
            <div
                ref={carTypesContainerRef}
                style={{
                    display: !isEmpty(carTypes) ? 'block' : 'none',
                    width: 'calc(100% - 80px * 2)'
                }}
            >
                <div className="car-model-selector__divider" />
                <div className="car-types-container car-model-selector__types-container">
                    {carTypes.map((option: CarType) => (
                        <FilterImageCheckbox
                            className="car-model-selector__checkbox car-model-selector__checkbox--compact"
                            check={
                                selectedOptions.carTypes
                                    ? selectedOptions.carTypes.includes(
                                          (option.key as string) || option.type
                                      )
                                    : false
                            }
                            disabled={option.count === 0}
                            hint={String(option.count)}
                            key={
                                option.key +
                                String(option.count) +
                                option['@id']
                            }
                            label={option.name}
                            onCheck={(value, event) => {
                                onSelect(
                                    'carTypes',
                                    option.key || option.type,
                                    event
                                );
                            }}
                            title="car-type-checkbox"
                            src={option.imageUrl}
                            value={option.key}
                            withImage
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
